import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { TrendingUp, AlertCircle, Users, MapPin, BarChart3 } from 'lucide-react';
import { Separator } from '../ui/separator';

export const ConversionFunnelCard = ({ conversionFunnel }) => {
  if (!conversionFunnel) {
    return (
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />Trial Conversion Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">Loading funnel data...</div>
        </CardContent>
      </Card>
    );
  }

  const colors = ['bg-blue-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-purple-500'];

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />Trial Conversion Funnel
        </CardTitle>
        <CardDescription>Track user journey from signup to paid conversion</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="relative">
            {conversionFunnel.funnel?.stages?.map((stage, idx) => {
              const width = Math.max(20, stage.rate);
              return (
                <div key={stage.name} className="flex items-center gap-4 mb-3">
                  <div className="w-40 text-sm font-medium text-right">{stage.name}</div>
                  <div className="flex-1 relative h-10">
                    <div 
                      className={`h-full ${colors[idx]} rounded-r-lg transition-all duration-500 flex items-center justify-end pr-3`}
                      style={{ width: `${width}%` }}
                    >
                      <span className="text-white font-bold text-sm">{stage.count}</span>
                    </div>
                  </div>
                  <div className="w-16 text-sm text-muted-foreground">{stage.rate}%</div>
                </div>
              );
            })}
            {conversionFunnel.funnel?.churned && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                <div className="w-40 text-sm font-medium text-right text-rose-600">Churned</div>
                <div className="flex-1 relative h-10">
                  <div 
                    className="h-full bg-rose-500 rounded-r-lg flex items-center justify-end pr-3"
                    style={{ width: `${Math.max(10, conversionFunnel.funnel.churned.rate)}%` }}
                  >
                    <span className="text-white font-bold text-sm">{conversionFunnel.funnel.churned.count}</span>
                  </div>
                </div>
                <div className="w-16 text-sm text-rose-600">{conversionFunnel.funnel.churned.rate}%</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{conversionFunnel.metrics?.total_trials || 0}</p>
              <p className="text-xs text-muted-foreground">Total Trials</p>
            </div>
            <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{conversionFunnel.metrics?.conversion_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Conversion Rate</p>
            </div>
            <div className="text-center p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{conversionFunnel.metrics?.engagement_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Engagement Rate</p>
            </div>
            <div className="text-center p-3 bg-rose-50 dark:bg-rose-950/30 rounded-lg">
              <p className="text-2xl font-bold text-rose-600">{conversionFunnel.metrics?.churn_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Churn Rate</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-lg font-semibold">{conversionFunnel.time_series?.conversions_30d || 0}</p>
              <p className="text-xs text-muted-foreground">Conversions (30d)</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold">{conversionFunnel.time_series?.conversions_60d || 0}</p>
              <p className="text-xs text-muted-foreground">Conversions (60d)</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold">{conversionFunnel.time_series?.conversions_90d || 0}</p>
              <p className="text-xs text-muted-foreground">Conversions (90d)</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const InsightsCard = ({ insights }) => {
  if (!insights || insights.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />Actionable Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight, i) => {
          const bgClass = insight.type === 'critical' ? 'bg-rose-50 border-rose-500 dark:bg-rose-950/20' :
            insight.type === 'warning' ? 'bg-amber-50 border-amber-500 dark:bg-amber-950/20' :
            insight.type === 'success' ? 'bg-emerald-50 border-emerald-500 dark:bg-emerald-950/20' :
            'bg-blue-50 border-blue-500 dark:bg-blue-950/20';
          return (
            <div key={i} className={`p-4 rounded-lg border-l-4 ${bgClass}`}>
              <p className="font-semibold text-sm">{insight.title}</p>
              <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
              <p className="text-xs mt-2 font-medium text-primary">Action: {insight.action}</p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export const EngagementBreakdownCard = ({ engagement }) => {
  if (!engagement) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4" />User Engagement Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="p-3 border rounded-lg text-center">
            <p className="text-xl font-bold text-rose-500">{engagement.never_logged_in}</p>
            <p className="text-xs text-muted-foreground">Never Logged In</p>
          </div>
          <div className="p-3 border rounded-lg text-center">
            <p className="text-xl font-bold text-amber-500">{engagement.logged_in_once}</p>
            <p className="text-xs text-muted-foreground">Logged In</p>
          </div>
          <div className="p-3 border rounded-lg text-center">
            <p className="text-xl font-bold text-blue-500">{engagement.created_rooms}</p>
            <p className="text-xs text-muted-foreground">Created Rooms</p>
          </div>
          <div className="p-3 border rounded-lg text-center">
            <p className="text-xl font-bold text-cyan-500">{engagement.created_reservations}</p>
            <p className="text-xs text-muted-foreground">Made Reservations</p>
          </div>
          <div className="p-3 border rounded-lg text-center">
            <p className="text-xl font-bold text-purple-500">{engagement.created_guests}</p>
            <p className="text-xs text-muted-foreground">Added Guests</p>
          </div>
          <div className="p-3 border rounded-lg text-center bg-emerald-50 dark:bg-emerald-950/20">
            <p className="text-xl font-bold text-emerald-500">{engagement.full_setup}</p>
            <p className="text-xs text-muted-foreground">Full Setup Complete</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const GeographyCard = ({ geoData }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />Hotels by City
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {geoData?.by_city?.slice(0, 10).map((c) => (
          <div key={c.city} className="flex items-center justify-between">
            <span>{c.city}</span>
            <div className="flex items-center gap-2">
              <Progress value={(c.count / Math.max(geoData?.by_city?.[0]?.count || 1, 1)) * 100} className="w-24 h-2" />
              <Badge variant="secondary">{c.count}</Badge>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export const FeatureAdoptionCard = ({ featureStats }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />Feature Adoption
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between">
          <span>Transfer Module Usage</span>
          <span className="font-medium">{featureStats?.transfers?.total || 0} transfers</span>
        </div>
        <div className="flex justify-between">
          <span>Hotels Using Transfers</span>
          <span className="font-medium">{featureStats?.transfers?.hotels_using || 0}</span>
        </div>
        <div className="flex justify-between">
          <span>Adoption Rate</span>
          <Badge variant="outline">{featureStats?.transfers?.adoption_rate || 0}%</Badge>
        </div>
        <Separator />
        <div className="flex justify-between">
          <span>Avg Rooms/Hotel</span>
          <span className="font-medium">{featureStats?.rooms?.average_per_hotel || 0}</span>
        </div>
        <div className="flex justify-between">
          <span>Total Reservations</span>
          <span className="font-medium">{featureStats?.reservations?.total || 0}</span>
        </div>
      </CardContent>
    </Card>
  );
};
